import { createElement, useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import SerpContentSnapshot from '@/components/Approval/@components/SerpContentSnapshot';
import type { ElementTransform } from '@/components/Common/HtmlToReact';
import {
  LEGACY_SALES_CONTRACT_OZ,
  openSalesContractOzReport,
  type SalesContractOzPrint,
} from '@/utils/approval/salesContract';
import { ACTION_CELL_CLASS, CARD_CLASS, PRINT_CELL_CLASS } from './content';
import { SalesContractRoot } from './styles';

export { buildSalesContractHtml } from './content';

/** 레거시 문서 카드의 버튼 */
const LEGACY_PRINT_CLASS = 'btn-printsc';
const LEGACY_DELETE_CLASS = 'btn-detail-hide';
const LEGACY_REJECT_CLASS = 'btn-detail-reject';

export type SalesContractTarget = { itemId: string; label: string };
export type SalesContractCardRef = SalesContractTarget & { aprvRev: string };

/** 부분반려 버튼을 붙일 카드 수 — V2 카드 + 레거시 부분반려 버튼 */
export const countSalesContractCards = (content: string | null) =>
  content
    ? (
        content.match(
          new RegExp(`${CARD_CLASS}"|${LEGACY_REJECT_CLASS}`, 'g'),
        ) ?? []
      ).length
    : 0;

/**
 * 저장 카드 HTML 그대로(레거시 문서 포함). Print 는 작성·조회 모두,
 * 삭제는 작성 화면에서만
 */
export const SalesContractSnapshot = ({
  content,
  onDelete,
  renderCardAction,
}: {
  content: string;
  onDelete?: (target: SalesContractTarget) => void;
  renderCardAction?: (card: SalesContractCardRef) => ReactNode;
}) => {
  const { t } = useTranslation();

  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props }) => {
      const { classList } = element;
      const printButton = (oz: SalesContractOzPrint) => (
        <BoxButton
          key="print"
          type="button"
          variant="secondary"
          size="small"
          onClick={() => openSalesContractOzReport(oz)}
        >
          Print
        </BoxButton>
      );
      if (classList.contains(PRINT_CELL_CLASS)) {
        const oz: SalesContractOzPrint = {
          url: element.getAttribute('data-oz-url'),
          gerpOzrNm: element.getAttribute('data-oz-ozr'),
          gerpOdiNm: element.getAttribute('data-oz-odi'),
          pcount: element.getAttribute('data-oz-pcount'),
          args1: element.getAttribute('data-oz-args1'),
        };
        return createElement(tagName, props, oz.args1 ? printButton(oz) : null);
      }
      if (classList.contains(ACTION_CELL_CLASS)) {
        const card = element.closest(`.${CARD_CLASS}`);
        const target = {
          itemId: card?.getAttribute('data-item-id') ?? '',
          label: card?.getAttribute('data-label') ?? '',
        };
        return createElement(
          tagName,
          props,
          onDelete && target.itemId ? (
            <BoxButton
              key="delete"
              type="button"
              variant="secondary"
              size="small"
              onClick={() => onDelete(target)}
            >
              {t('삭제')}
            </BoxButton>
          ) : null,
          target.itemId
            ? renderCardAction?.({
                ...target,
                aprvRev: card?.getAttribute('data-aprv-rev') ?? '',
              }) ?? null
            : null,
        );
      }
      if (classList.contains(LEGACY_PRINT_CLASS)) {
        const contractNo = element.getAttribute('data-sales_contr_no') ?? '';
        return contractNo
          ? printButton({
              ...LEGACY_SALES_CONTRACT_OZ,
              args1: `sales_contr_no=${contractNo}`,
            })
          : null;
      }
      if (classList.contains(LEGACY_REJECT_CLASS)) {
        const itemId = element.getAttribute('data-aprv_id') ?? '';
        return itemId
          ? renderCardAction?.({
              itemId,
              label: element.getAttribute('data-sales_contr_no') ?? '',
              aprvRev: element.getAttribute('data-aprv_rev') ?? '',
            }) ?? null
          : null;
      }
      if (classList.contains(LEGACY_DELETE_CLASS)) return null;
      return undefined;
    },
    [onDelete, renderCardAction, t],
  );

  return (
    <SalesContractRoot>
      <SerpContentSnapshot content={content} transform={transform} />
    </SalesContractRoot>
  );
};
